'use client'

import { AlertIcon } from '@repo/ui/AlertIcon'
import { ArchivedIcon } from '@repo/ui/ArchivedIcon'
import { Avatar } from '@repo/ui/Avatar'
import { CheckIcon } from '@repo/ui/CheckIcon'
import { ClientNameAndMatterDescription } from '../../lockups/ClientNameAndMatterDescription/ClientNameAndMatterDescription'
import { LockIcon } from '@repo/ui/LockIcon'
import { useClientStore } from '@repo/store/useClientStore'
import { useEmailCardStore } from '@repo/store/useEmailCardStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import PropTypes from 'prop-types'
import styles from './MiniMatterCard.module.scss'

// This component is a simple card which renders details of a Matter. It gracefully handles displaying whatever data is supplied to it - only the Client No is required

export const MiniMatterCard = (props) => {
  const {
    clientNo,
    matterNo,
    forename,
    surname,
    matterDescription,
    feeEarnerId,
    feeEarnerDescription,
    clickHandler,
    ospreyMatter_DI,
    dateArchived,
    showMatterPill,
    disableDefaultClickHandler = false,
    isUserRestricted = false,
    highRisk,
    isEmailCard,
    workflowTypeId,
    sx = {}
  } = props

  const { currentClientMatterNo, setCurrentMatter } = useMattersStore()
  const { navigateWithClientMatterNo } = useUrlStore()
  const { setCurrentClient } = useClientStore()
  const { setEmailSendingCurrentMatterData } = useEmailCardStore()
  const clientMatterNo = `${clientNo}-${matterNo}`
  const isCurrentMatter = clientMatterNo === currentClientMatterNo
  const isArchived = Boolean(dateArchived)

  const onClick = (e, { clientNo, matterNo }) => {
    e.stopPropagation()

    if (isUserRestricted) return

    if (clickHandler) {
      const matter = {
        clientNo,
        matterNo: Number(matterNo || 0),
        forename,
        surname,
        matterDescription,
        feeEarnerDescription,
        dateArchived,
        isUserRestricted,
        highRisk,
        workflowTypeId
      }

      clickHandler(matter)
    }

    if (disableDefaultClickHandler) return

    if (isEmailCard) {
      const clientMatterNo = `${clientNo}-${matterNo}`
      setCurrentMatter({ clientMatterNo, ospreyMatter_DI })
      setCurrentClient({ clientNo, ospreyClient_DI: ospreyMatter_DI })

      setEmailSendingCurrentMatterData({
        clientMatterNo: clientMatterNo,
        clientNo: clientNo,
        dateArchived: dateArchived,
        feeEarnerDescription: feeEarnerDescription,
        forename: forename,
        highRisk: highRisk,
        isArchived: isArchived,
        isUserRestricted: isUserRestricted,
        matterDescription: matterDescription,
        matterNo: matterNo,
        showMatterPill: showMatterPill,
        surname: surname,
        workTypeId: workflowTypeId
      })
    } else {
      // go to default matter page
      navigateWithClientMatterNo({ clientMatterNo })
    }
  }

  if (!clientNo) return null

  return (
    <div
      className={`${styles.miniMatterCardButton} ${isCurrentMatter ? styles.currentMatter : ''}`}
      data-testid="miniMatterCardButton"
    >
      <MUITooltip title={isUserRestricted ? 'You are not authorised to view this matter' : ''} placement="top" arrow>
        <MUIStack
          direction="row"
          alignItems="center"
          className={styles.miniMatterCard}
          onClick={(e) => onClick(e, { clientNo, matterNo })}
          data-testid="miniMatterCard"
          sx={{
            textAlign: 'left',
            opacity: isUserRestricted ? 0.6 : 1,
            cursor: isUserRestricted ? 'not-allowed' : 'pointer',
            ...sx
          }}
        >
          <Avatar sx={{ marginRight: 2 }} feeEarnerId={feeEarnerId} name={feeEarnerDescription} />
          <ClientNameAndMatterDescription
            clientNo={clientNo}
            matterNo={matterNo}
            forename={forename}
            surname={surname}
            matterDescription={matterDescription}
            isCurrentMatter={isCurrentMatter}
            dateArchived={dateArchived}
            showMatterPill={showMatterPill}
          />

          <MUIStack direction="row" justifyContent="flex-end" sx={{ width: '78px' }} data-testid="miniMatterCardIcons">
            {isUserRestricted ? (
              <MUIStack>
                <LockIcon sx={{ width: '18px', height: '18px', ml: 1, mr: 1 }} />
              </MUIStack>
            ) : null}

            {!isUserRestricted && isArchived ? (
              <MUITooltip title="Archived" placement="top" arrow>
                <MUIStack>
                  <ArchivedIcon sx={{ width: '18px', height: '18px', ml: 1, mr: 1 }} />
                </MUIStack>
              </MUITooltip>
            ) : null}

            {!isUserRestricted && highRisk ? (
              <MUITooltip title="High risk" placement="top" arrow>
                <MUIStack>
                  <AlertIcon sx={{ width: '18px', height: '18px', ml: 1, mr: 1 }} />
                </MUIStack>
              </MUITooltip>
            ) : null}

            {!isUserRestricted && isCurrentMatter ? (
              <MUITooltip title="Current matter" placement="top" arrow>
                <MUIStack>
                  <CheckIcon sx={{ width: '18px', height: '18px', ml: 1, mr: 1, opacity: 1 }} />
                </MUIStack>
              </MUITooltip>
            ) : null}
          </MUIStack>
        </MUIStack>
      </MUITooltip>
    </div>
  )
}

MiniMatterCard.propTypes = {
  clickHandler: PropTypes.func,
  clientNo: PropTypes.string.isRequired,
  matterNo: PropTypes.any,
  forename: PropTypes.string,
  surname: PropTypes.string,
  matterDescription: PropTypes.string,
  feeEarnerDescription: PropTypes.string,
  feeEarnerId: PropTypes.string,
  dateArchived: PropTypes.string,
  ospreyMatter_DI: PropTypes.func,
  showMatterPill: PropTypes.bool,
  disableDefaultClickHandler: PropTypes.bool,
  isUserRestricted: PropTypes.bool,
  highRisk: PropTypes.bool,
  isEmailCard: PropTypes.bool,
  workflowTypeId: PropTypes.string,
  sx: PropTypes.object
}
